import { CheckCircle2, Circle, Clock, MessageSquare, User } from 'lucide-react';
import React from 'react';
import { Link } from 'react-router-dom';

import type { PillVariant } from '@/components/common/Pill';
import { Pill } from '@/components/common/Pill';
import { cn } from '@/lib/utils';
import { tasksApiClient } from '@/services/api/tasksService';

interface MiniTortTasksProps {
  matterId: string;
  className?: string;
}

interface TaskShape {
  id: string;
  name: string;
  status: { name: string; canonical_status?: string } | string;
  assignee?: { full_name?: string; first_name?: string } | null;
  due_date?: string | null;
  type?: { name: string } | null;
  updates_count?: number;
}

function getCanonicalStatus(task: TaskShape): string {
  if (typeof task.status === 'string') return task.status;
  return task.status?.canonical_status ?? task.status?.name ?? 'UNKNOWN';
}

function getStatusLabel(task: TaskShape): string {
  if (typeof task.status === 'string') return task.status;
  return task.status?.name ?? 'Unknown';
}

function getAssigneeName(task: TaskShape): string | null {
  if (!task.assignee) return null;
  return task.assignee.full_name ?? task.assignee.first_name ?? null;
}

function getTypeName(task: TaskShape): string | null {
  if (!task.type) return null;
  return task.type.name;
}

function getTaskStatusVariant(canonical: string): PillVariant {
  const upper = canonical.toUpperCase();
  if (upper === 'COMPLETED' || upper === 'CLOSED') return 'green';
  if (upper === 'IN_PROGRESS' || upper === 'PENDING') return 'purple';
  if (upper === 'BLOCKED') return 'red';
  return 'gray';
}

function isTaskOpen(canonical: string): boolean {
  const upper = canonical.toUpperCase();
  return !['COMPLETED', 'CLOSED', 'NOT_APPLICABLE'].includes(upper);
}

export const MiniTortTasks: React.FC<MiniTortTasksProps> = ({ matterId, className }) => {
  const { data, isLoading } = tasksApiClient.useGetTasksQuery(
    { matter: [matterId] },
    { refetchOnMountOrArgChange: 30 }, // re-fetch if stale > 30s (covers post-action refresh)
  );

  if (isLoading) {
    return (
      <div className={cn('rounded-lg border border-zinc-200 bg-white p-4 dark:border-zinc-800 dark:bg-zinc-900', className)}>
        <h3 className="text-sm font-semibold text-zinc-900 dark:text-white">Tasks</h3>
        <p className="mt-2 text-sm text-zinc-500">Loading tasks...</p>
      </div>
    );
  }

  // data is TaskSearchResults { results: Task[], count } or possibly an array
  const rawResults = (data as { results?: TaskShape[] })?.results ?? (data as TaskShape[] | undefined) ?? [];
  const tasks: TaskShape[] = rawResults;

  const openTasks = tasks.filter((t) => isTaskOpen(getCanonicalStatus(t)));
  const closedTasks = tasks.filter((t) => !isTaskOpen(getCanonicalStatus(t)));

  return (
    <div className={cn('rounded-lg border border-zinc-200 bg-white p-4 dark:border-zinc-800 dark:bg-zinc-900', className)}>
      <div className="mb-3 flex items-center justify-between">
        <h3 className="text-sm font-semibold text-zinc-900 dark:text-white">Tasks</h3>
        <span className="text-xs font-medium text-zinc-500 dark:text-zinc-400">
          {openTasks.length} open{closedTasks.length > 0 ? `, ${closedTasks.length} closed` : ''}
        </span>
      </div>

      {tasks.length === 0 && (
        <p className="text-sm text-zinc-500 dark:text-zinc-400">No tasks on this matter</p>
      )}

      {/* Open tasks */}
      {openTasks.length > 0 && (
        <div className="space-y-2">
          {openTasks.map((task) => (
            <TaskRow key={task.id} task={task} matterId={matterId} />
          ))}
        </div>
      )}

      {/* Closed tasks (collapsed) */}
      {closedTasks.length > 0 && (
        <details className={cn(openTasks.length > 0 && 'mt-3')}>
          <summary className="cursor-pointer text-xs font-medium text-zinc-500 hover:text-zinc-700 dark:text-zinc-400 dark:hover:text-zinc-300">
            {closedTasks.length} completed task{closedTasks.length !== 1 ? 's' : ''}
          </summary>
          <div className="mt-2 space-y-2">
            {closedTasks.map((task) => (
              <TaskRow key={task.id} task={task} matterId={matterId} closed />
            ))}
          </div>
        </details>
      )}
    </div>
  );
};

const TaskRow: React.FC<{ task: TaskShape; matterId: string; closed?: boolean }> = ({
  task,
  matterId,
  closed,
}) => {
  const canonical = getCanonicalStatus(task);
  const statusLabel = getStatusLabel(task);
  const assignee = getAssigneeName(task);
  const typeName = getTypeName(task);

  return (
    <Link
      to={`/matters/${matterId}/tasks/${task.id}`}
      className={cn(
        'flex items-start gap-2.5 rounded-md border px-3 py-2 transition-colors hover:bg-zinc-50 dark:hover:bg-zinc-800/50',
        closed
          ? 'border-zinc-100 bg-zinc-50/50 dark:border-zinc-800 dark:bg-zinc-800/30'
          : 'border-zinc-200 bg-white dark:border-zinc-700 dark:bg-zinc-900',
      )}
    >
      <div className="mt-0.5 shrink-0">
        {closed ? (
          <CheckCircle2 className="h-4 w-4 text-emerald-500" />
        ) : (
          <Circle className="h-4 w-4 text-indigo-500" />
        )}
      </div>
      <div className="flex-1 min-w-0">
        <p className={cn('text-sm font-medium', closed ? 'text-zinc-500 line-through dark:text-zinc-400' : 'text-zinc-800 dark:text-zinc-200')}>
          {task.name}
        </p>
        <div className="mt-1 flex flex-wrap items-center gap-2">
          <Pill variant={getTaskStatusVariant(canonical)} size="sm">
            {statusLabel.replace(/_/g, ' ')}
          </Pill>
          {typeName && (
            <span className="text-[10px] font-medium text-zinc-500 dark:text-zinc-400">{typeName}</span>
          )}
          {assignee && (
            <span className="flex items-center gap-0.5 text-[10px] font-medium text-zinc-500 dark:text-zinc-400">
              <User className="h-3 w-3" />
              {assignee}
            </span>
          )}
          {task.due_date && (
            <span className="flex items-center gap-0.5 text-[10px] font-medium text-zinc-500 dark:text-zinc-400">
              <Clock className="h-3 w-3" />
              {new Date(task.due_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
            </span>
          )}
          {task.updates_count != null && task.updates_count > 0 && (
            <span className="flex items-center gap-0.5 text-[10px] font-medium text-zinc-500 dark:text-zinc-400">
              <MessageSquare className="h-3 w-3" />
              {task.updates_count}
            </span>
          )}
        </div>
      </div>
    </Link>
  );
};
